import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VideocamOffIcon from "@mui/icons-material/VideocamOffOutlined";
import { useCallback, useEffect, useState } from "react";
import {
  emptyCareProfileInput,
  type CareProfileInput,
  type CareTeamMember,
} from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";

interface Props {
  open: boolean;
  premiseId: string;
  /** The resident whose profile we're editing (PhiRecord id). */
  personRef: string;
  /** Human label for the room, for the dialog title. */
  roomLabel: string;
  onClose: () => void;
  /** Fired after a successful save so the parent can surface a notice. */
  onSaved?: () => void;
}

/**
 * Staff authoring for a resident's in-room clinical whiteboard: allergies,
 * isolation, fall risk, diet, precautions, care team and a free-text goal.
 * The profile is keyed to the resident (personRef) and carries no identifiers,
 * so this is gated on `canManageCareProfiles` (same as the roster) - not PHI
 * reveal. Loads the current profile on open; an unauthored resident starts blank.
 */
export default function CareProfileDialog({
  open,
  premiseId,
  personRef,
  roomLabel,
  onClose,
  onSaved,
}: Props) {
  const [draft, setDraft] = useState<CareProfileInput>(emptyCareProfileInput());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await foyerApi.getCareProfile(premiseId, personRef);
      setDraft({
        allergies: p.allergies,
        isolation: p.isolation,
        fallRisk: p.fallRisk,
        diet: p.diet,
        precautions: p.precautions,
        careTeam: p.careTeam,
        notes: p.notes,
        // The resident-level video-call opt-out (§4.8). Round-tripped so the
        // toggle reflects the stored value and a Save can't silently re-enable
        // calls by omitting it.
        videoCallsDisabled: p.videoCallsDisabled,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [premiseId, personRef]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const patch = (p: Partial<CareProfileInput>) =>
    setDraft((d) => ({ ...d, ...p }));

  const setTeamMember = (i: number, m: Partial<CareTeamMember>) =>
    patch({
      careTeam: draft.careTeam.map((t, j) => (i === j ? { ...t, ...m } : t)),
    });
  const addTeamMember = () =>
    patch({ careTeam: [...draft.careTeam, { role: "", name: "" }] });
  const removeTeamMember = (i: number) =>
    patch({ careTeam: draft.careTeam.filter((_, j) => j !== i) });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await foyerApi.saveCareProfile(premiseId, personRef, draft);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="care-profile-dialog"
    >
      <DialogTitle>Care profile - {roomLabel}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Resident-level video-call opt-out (design §4.8). Deliberately the
                FIRST control in the dialog and given its own bordered, tinted
                box - it is a privacy decision that overrides every other setting
                (premise policy, contact flags, staff), so it must never read as
                one clinical toggle among many. */}
            <Box
              sx={{
                border: 1,
                borderColor: draft.videoCallsDisabled
                  ? "error.main"
                  : "divider",
                bgcolor: draft.videoCallsDisabled
                  ? "error.light"
                  : "action.hover",
                borderRadius: 1,
                p: 2,
              }}
            >
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start" }}
                control={
                  <Switch
                    checked={draft.videoCallsDisabled ?? false}
                    onChange={(e) =>
                      patch({ videoCallsDisabled: e.target.checked })
                    }
                    color="error"
                    data-testid="profile-video-calls-disabled"
                  />
                }
                label={
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <VideocamOffIcon fontSize="small" />
                      Turn off video calls for this resident
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      A resident-level opt-out that overrides everything -
                      premise policy, contact permissions, and staff. When on,
                      this resident cannot be called by anyone and cannot call
                      out, and the room&apos;s call button disappears.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={draft.allergies}
              onChange={(_, v) => patch({ allergies: v as string[] })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Allergies"
                  placeholder="Type and press Enter"
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": "profile-allergies",
                  }}
                />
              )}
            />

            <TextField
              size="small"
              label="Isolation precaution"
              placeholder="e.g. Contact, Droplet - leave blank for none"
              value={draft.isolation ?? ""}
              onChange={(e) => patch({ isolation: e.target.value || null })}
              fullWidth
              inputProps={{ "data-testid": "profile-isolation" }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={draft.fallRisk}
                  onChange={(e) => patch({ fallRisk: e.target.checked })}
                  data-testid="profile-fallrisk"
                />
              }
              label="Fall risk"
            />

            <TextField
              size="small"
              label="Diet"
              placeholder="e.g. Pureed, Diabetic - leave blank for none"
              value={draft.diet ?? ""}
              onChange={(e) => patch({ diet: e.target.value || null })}
              fullWidth
              inputProps={{ "data-testid": "profile-diet" }}
            />

            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={draft.precautions}
              onChange={(_, v) => patch({ precautions: v as string[] })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Precautions"
                  placeholder="Type and press Enter"
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": "profile-precautions",
                  }}
                />
              )}
            />

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Care team
              </Typography>
            </Divider>
            <Stack spacing={1.5}>
              {draft.careTeam.map((m, i) => (
                <Stack direction="row" spacing={1} key={i} alignItems="center">
                  <TextField
                    size="small"
                    label="Role"
                    value={m.role}
                    onChange={(e) => setTeamMember(i, { role: e.target.value })}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    size="small"
                    label="Name"
                    value={m.name}
                    onChange={(e) => setTeamMember(i, { name: e.target.value })}
                    fullWidth
                  />
                  <IconButton
                    aria-label="Remove care-team member"
                    onClick={() => removeTeamMember(i)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addTeamMember}
                sx={{ alignSelf: "flex-start" }}
                data-testid="profile-add-team"
              >
                Add care-team member
              </Button>
            </Stack>

            <TextField
              size="small"
              label="Care goal / note"
              placeholder="Shown to the resident and family in the room"
              value={draft.notes ?? ""}
              onChange={(e) => patch({ notes: e.target.value || null })}
              fullWidth
              multiline
              minRows={2}
              inputProps={{ "data-testid": "profile-notes" }}
            />

            <Typography variant="caption" color="text.secondary">
              Shown on the in-room care whiteboard. The profile follows the
              resident across rooms and re-admits, and is removed if the
              resident is erased. Edits are recorded in the compliance audit
              trail. Relatives the resident can video-call live on{" "}
              <strong>Family</strong> on this resident’s row - they belong to
              the stay, not to the profile, and are revoked at discharge.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loading}
          data-testid="profile-save"
        >
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
